import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { authFetch } from "@/lib/authFetch";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Folder,
  File as FileIcon,
  FileText,
  FileImage,
  Film,
  Archive,
  Home,
  ChevronRight,
  LayoutGrid,
  List,
  Upload,
  FolderPlus,
  MoreVertical,
  Pencil,
  Move,
  Download,
  Trash2,
  Loader2,
  GripVertical,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { ResellerFolder, ResellerFile } from "@shared/schema";

interface FolderWithCount extends ResellerFolder {
  childCount?: number;
}

interface BreadcrumbItem {
  id: string | null;
  nom: string;
}

interface FolderContents {
  folders: FolderWithCount[];
  files: ResellerFile[];
  breadcrumb: BreadcrumbItem[];
}

interface UploadItem {
  id: string;
  file: File;
  progress: number;
  done: boolean;
  error?: string;
}

function fileIcon(typeFichier: string | null, nom: string) {
  const mime = typeFichier || "";
  const ext = nom.split(".").pop()?.toLowerCase() || "";
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return FileImage;
  if (mime.startsWith("video/") || ["mp4", "avi", "mov", "mkv"].includes(ext)) return Film;
  if (mime === "application/pdf" || ext === "pdf") return FileText;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return Archive;
  if (["doc", "docx", "txt", "odt"].includes(ext)) return FileText;
  return FileIcon;
}

function formatSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return format(new Date(iso), "dd/MM/yyyy", { locale: fr });
  } catch {
    return "";
  }
}

const VIEW_MODE_KEY = "reseller-explorer-view";

export function ResellerFileExplorer({
  resellerId,
  canEdit,
}: {
  resellerId: string;
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    return (localStorage.getItem(VIEW_MODE_KEY) as "grid" | "list") || "grid";
  });

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("Nouveau dossier");
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  const [renaming, setRenaming] = useState<{ id: string; type: "folder" | "file"; nom: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  const [moveTarget, setMoveTarget] = useState<{ id: string; type: "folder" | "file"; nom: string } | null>(null);
  const [moveFolderId, setMoveFolderId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; type: "folder" | "file"; nom: string } | null>(null);

  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [draggedItem, setDraggedItem] = useState<{ id: string; type: "folder" | "file" } | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  const [listDragIndex, setListDragIndex] = useState<{ type: "folder" | "file"; index: number } | null>(null);
  const [listDragOverIndex, setListDragOverIndex] = useState<number | null>(null);

  const qkFolders = ["/api/resellers", resellerId, "folders", currentFolderId];

  const { data, isLoading } = useQuery<FolderContents>({
    queryKey: qkFolders,
    queryFn: async () => {
      const url = `/api/resellers/${resellerId}/folders${currentFolderId ? `?folder_id=${currentFolderId}` : ""}`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error("Erreur chargement");
      return r.json();
    },
  });

  const folders = data?.folders ?? [];
  const files = data?.files ?? [];
  const breadcrumb = data?.breadcrumb ?? [{ id: null, nom: "Documents" }];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["/api/resellers", resellerId, "folders"] });
  }

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (creatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
      newFolderInputRef.current.select();
    }
  }, [creatingFolder]);

  useEffect(() => {
    if (renaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renaming]);

  async function handleCreateFolder() {
    const nom = newFolderName.trim();
    if (!nom) { setCreatingFolder(false); return; }
    try {
      const r = await authFetch(`/api/resellers/${resellerId}/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom, parent_id: currentFolderId }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setCreatingFolder(false);
      setNewFolderName("Nouveau dossier");
      invalidate();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  }

  async function handleRename() {
    if (!renaming) return;
    const nom = renameValue.trim();
    if (!nom) { setRenaming(null); return; }
    try {
      const endpoint = renaming.type === "folder"
        ? `/api/resellers/${resellerId}/folders/${renaming.id}`
        : `/api/resellers/${resellerId}/files/${renaming.id}`;
      const r = await authFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setRenaming(null);
      invalidate();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  }

  async function handleMove() {
    if (!moveTarget) return;
    try {
      const endpoint = moveTarget.type === "folder"
        ? `/api/resellers/${resellerId}/folders/${moveTarget.id}`
        : `/api/resellers/${resellerId}/files/${moveTarget.id}`;
      const body = moveTarget.type === "folder"
        ? { parent_id: moveFolderId }
        : { folder_id: moveFolderId };
      const r = await authFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setMoveTarget(null);
      setMoveFolderId(null);
      invalidate();
      toast({ title: "Déplacé" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      const endpoint = deleteTarget.type === "folder"
        ? `/api/resellers/${resellerId}/folders/${deleteTarget.id}`
        : `/api/resellers/${resellerId}/files/${deleteTarget.id}`;
      const r = await authFetch(endpoint, { method: "DELETE" });
      if (!r.ok) throw new Error((await r.json()).message);
      setDeleteTarget(null);
      invalidate();
      toast({ title: deleteTarget.type === "folder" ? "Dossier supprimé" : "Fichier supprimé" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  }

  async function uploadFiles(fileList: File[]) {
    const MAX = 50 * 1024 * 1024;
    const newItems: UploadItem[] = fileList.map(f => ({ id: crypto.randomUUID(), file: f, progress: 0, done: false }));
    setUploadQueue(q => [...q, ...newItems]);

    for (const item of newItems) {
      if (item.file.size > MAX) {
        setUploadQueue(q => q.map(i => i.id === item.id ? { ...i, done: true, error: "Limite 50 Mo dépassée" } : i));
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("nom", item.file.name);
        if (currentFolderId) fd.append("folder_id", currentFolderId);
        const xhr = new XMLHttpRequest();
        await new Promise<void>((resolve, reject) => {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 100);
              setUploadQueue(q => q.map(i => i.id === item.id ? { ...i, progress: pct } : i));
            }
          };
          xhr.onload = () => {
            if (xhr.status < 300) {
              setUploadQueue(q => q.map(i => i.id === item.id ? { ...i, done: true, progress: 100 } : i));
              resolve();
            } else {
              try {
                const body = JSON.parse(xhr.responseText);
                reject(new Error(body.message || `Erreur ${xhr.status}`));
              } catch {
                reject(new Error(`Erreur upload (HTTP ${xhr.status})`));
              }
            }
          };
          xhr.onerror = () => reject(new Error("Erreur réseau"));
          xhr.open("POST", `/api/resellers/${resellerId}/files`);
          supabase.auth.getSession().then(({ data }) => {
            const token = data.session?.access_token;
            if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.send(fd);
          });
          return;
        });
      } catch (e: any) {
        setUploadQueue(q => q.map(i => i.id === item.id ? { ...i, done: true, error: e.message } : i));
      }
    }
    invalidate();
    setTimeout(() => {
      setUploadQueue(q => q.filter(i => !i.done));
    }, 3000);
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (!canEdit) return;
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length) { uploadFiles(droppedFiles); return; }
    if (draggedItem) {
      const target = e.currentTarget.getAttribute("data-folder-id");
      if (target && target !== draggedItem.id) handleDropOnFolder(target);
    }
  }

  async function handleDropOnFolder(targetFolderId: string) {
    if (!draggedItem) return;
    try {
      const endpoint = draggedItem.type === "folder"
        ? `/api/resellers/${resellerId}/folders/${draggedItem.id}`
        : `/api/resellers/${resellerId}/files/${draggedItem.id}`;
      const body = draggedItem.type === "folder"
        ? { parent_id: targetFolderId }
        : { folder_id: targetFolderId };
      const r = await authFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setDraggedItem(null);
      setDragOverFolder(null);
      invalidate();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur déplacement", description: (e as any).message });
    }
  }

  async function handleListReorder(type: "folder" | "file", fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex) return;
    let items: { id: string; ordre: number }[];
    if (type === "folder") {
      const list = [...folders];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      items = list.map((f, i) => ({ id: f.id, ordre: i }));
    } else {
      const list = [...files];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      items = list.map((f, i) => ({ id: f.id, ordre: i }));
    }
    const endpoint = type === "folder"
      ? `/api/resellers/${resellerId}/folders/reorder`
      : `/api/resellers/${resellerId}/files/reorder`;
    await authFetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    invalidate();
  }

  function FolderContextMenu({ folder }: { folder: FolderWithCount }) {
    if (!canEdit) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" data-testid={`menu-folder-${folder.id}`}
            onClick={e => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => { setRenaming({ id: folder.id, type: "folder", nom: folder.nom }); setRenameValue(folder.nom); }}>
            <Pencil className="h-4 w-4 mr-2" /> Renommer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { setMoveTarget({ id: folder.id, type: "folder", nom: folder.nom }); setMoveFolderId(currentFolderId); }}>
            <Move className="h-4 w-4 mr-2" /> Déplacer
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget({ id: folder.id, type: "folder", nom: folder.nom })}>
            <Trash2 className="h-4 w-4 mr-2" /> Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function FileContextMenu({ file }: { file: ResellerFile }) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" data-testid={`menu-file-${file.id}`}
            onClick={e => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          {canEdit && (
            <DropdownMenuItem onClick={() => { setRenaming({ id: file.id, type: "file", nom: file.nom }); setRenameValue(file.nom); }}>
              <Pencil className="h-4 w-4 mr-2" /> Renommer
            </DropdownMenuItem>
          )}
          {canEdit && (
            <DropdownMenuItem onClick={() => { setMoveTarget({ id: file.id, type: "file", nom: file.nom }); setMoveFolderId(currentFolderId); }}>
              <Move className="h-4 w-4 mr-2" /> Déplacer
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <a href={file.fichierUrl} target="_blank" rel="noopener noreferrer" download>
              <Download className="h-4 w-4 mr-2" /> Télécharger
            </a>
          </DropdownMenuItem>
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget({ id: file.id, type: "file", nom: file.nom })}>
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="border-t pt-3 mt-3">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm mb-3 flex-wrap" aria-label="fil d'ariane">
        {breadcrumb.map((crumb, i) => (
          <span key={crumb.id ?? "root"} className="flex items-center gap-1">
            {i === 0 && (
              <button
                onClick={() => setCurrentFolderId(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="breadcrumb-home"
              >
                <Home className="h-4 w-4" />
              </button>
            )}
            {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            {i < breadcrumb.length - 1 ? (
              <button
                onClick={() => setCurrentFolderId(crumb.id)}
                className="text-muted-foreground hover:text-foreground hover:underline truncate max-w-[120px]"
                data-testid={`breadcrumb-${crumb.id}`}
              >
                {i > 0 ? crumb.nom : null}
              </button>
            ) : (
              <span className="font-medium truncate max-w-[160px]">{i > 0 ? crumb.nom : null}</span>
            )}
          </span>
        ))}
      </nav>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {canEdit && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setCreatingFolder(true); setNewFolderName("Nouveau dossier"); }}
              data-testid="button-new-folder"
            >
              <FolderPlus className="h-4 w-4 mr-1.5" /> Nouveau dossier
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="button-upload"
            >
              <Upload className="h-4 w-4 mr-1.5" /> Uploader
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onFileInputChange}
            />
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode("list")}
            data-testid="button-view-list"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "grid" ? "default" : "ghost"}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode("grid")}
            data-testid="button-view-grid"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className={`relative min-h-[200px] rounded-lg transition-colors ${isDragOver ? "bg-primary/10 border-2 border-dashed border-primary" : ""}`}
        onDragOver={e => { e.preventDefault(); if (e.dataTransfer.types.includes("Files") || draggedItem) setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary/10 pointer-events-none">
            <span className="text-primary font-medium">Déposez vos fichiers ici</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
          </div>
        ) : (
          <>
            {/* Inline folder creation */}
            {creatingFolder && (
              <div className={`flex items-center gap-2 mb-2 p-2 rounded-lg border bg-muted/30 ${viewMode === "grid" ? "w-[140px] inline-flex mr-2" : "w-full"}`}>
                <Folder className="h-5 w-5 text-yellow-500 shrink-0" />
                <Input
                  ref={newFolderInputRef}
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleCreateFolder();
                    if (e.key === "Escape") { setCreatingFolder(false); }
                  }}
                  onBlur={handleCreateFolder}
                  className="h-6 text-sm px-1 py-0"
                  data-testid="input-new-folder-name"
                />
              </div>
            )}

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="flex flex-wrap gap-3">
                {/* Folders */}
                {folders.map(folder => (
                  <div
                    key={folder.id}
                    className={`group relative w-[140px] rounded-lg border p-3 cursor-pointer flex flex-col items-center gap-2 hover:bg-muted/60 transition-colors ${dragOverFolder === folder.id ? "ring-2 ring-primary bg-primary/10" : ""}`}
                    data-testid={`folder-card-${folder.id}`}
                    draggable
                    data-folder-id={folder.id}
                    onDragStart={() => setDraggedItem({ id: folder.id, type: "folder" })}
                    onDragEnd={() => { setDraggedItem(null); setDragOverFolder(null); }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverFolder(folder.id); }}
                    onDragLeave={() => setDragOverFolder(null)}
                    onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragOverFolder(null); if (draggedItem && draggedItem.id !== folder.id) handleDropOnFolder(folder.id); }}
                    onDoubleClick={() => setCurrentFolderId(folder.id)}
                    onClick={() => {/* single click = select */}}
                  >
                    <div className="absolute top-1.5 right-1.5">
                      <FolderContextMenu folder={folder} />
                    </div>
                    {renaming?.id === folder.id ? (
                      <Input
                        ref={renameInputRef}
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(null); }}
                        onBlur={handleRename}
                        className="h-6 text-xs w-full px-1"
                        onClick={e => e.stopPropagation()}
                        data-testid={`input-rename-${folder.id}`}
                      />
                    ) : (
                      <>
                        <Folder className="h-10 w-10 text-yellow-400" />
                        <span className="text-xs text-center leading-tight break-words w-full line-clamp-2">{folder.nom}</span>
                        {folder.childCount !== undefined && (
                          <span className="text-[10px] text-muted-foreground">{folder.childCount} élément{folder.childCount !== 1 ? "s" : ""}</span>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {/* Files */}
                {files.map(file => {
                  const Icon = fileIcon(file.typeFichier, file.nom);
                  return (
                    <div
                      key={file.id}
                      className="group relative w-[140px] rounded-lg border p-3 cursor-pointer flex flex-col items-center gap-2 hover:bg-muted/60 transition-colors"
                      data-testid={`file-card-${file.id}`}
                      draggable
                      onDragStart={() => setDraggedItem({ id: file.id, type: "file" })}
                      onDragEnd={() => setDraggedItem(null)}
                      onDoubleClick={() => window.open(file.fichierUrl, "_blank")}
                    >
                      <div className="absolute top-1.5 right-1.5">
                        <FileContextMenu file={file} />
                      </div>
                      {renaming?.id === file.id ? (
                        <Input
                          ref={renameInputRef}
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(null); }}
                          onBlur={handleRename}
                          className="h-6 text-xs w-full px-1"
                          onClick={e => e.stopPropagation()}
                          data-testid={`input-rename-${file.id}`}
                        />
                      ) : (
                        <>
                          <Icon className="h-10 w-10 text-muted-foreground" />
                          <span className="text-xs text-center leading-tight break-words w-full line-clamp-2">{file.nom}</span>
                          <span className="text-[10px] text-muted-foreground">{formatSize(file.taille)}</span>
                        </>
                      )}
                    </div>
                  );
                })}

                {folders.length === 0 && files.length === 0 && !creatingFolder && (
                  <p className="text-muted-foreground text-sm py-4 w-full text-center">Dossier vide</p>
                )}
              </div>
            )}

            {/* List View */}
            {viewMode === "list" && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    {canEdit && <th className="w-8" />}
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Nom</th>
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">Type</th>
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">Taille</th>
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground hidden md:table-cell">Date</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {/* Folders rows */}
                  {folders.map((folder, idx) => (
                    <tr
                      key={folder.id}
                      className={`group border-b hover:bg-muted/40 cursor-pointer transition-colors ${listDragOverIndex === idx && listDragIndex?.type === "folder" ? "border-t-2 border-primary" : ""}`}
                      data-testid={`folder-row-${folder.id}`}
                      draggable
                      onDragStart={() => { setDraggedItem({ id: folder.id, type: "folder" }); setListDragIndex({ type: "folder", index: idx }); }}
                      onDragEnd={() => { setDraggedItem(null); setListDragIndex(null); setListDragOverIndex(null); }}
                      onDragOver={e => { e.preventDefault(); setDragOverFolder(folder.id); setListDragOverIndex(idx); }}
                      onDragLeave={() => { setDragOverFolder(null); }}
                      onDrop={e => {
                        e.preventDefault();
                        if (e.dataTransfer.files.length) { onDrop(e); return; }
                        if (listDragIndex?.type === "folder" && listDragOverIndex !== null) {
                          handleListReorder("folder", listDragIndex.index, idx);
                        } else if (draggedItem && draggedItem.id !== folder.id) {
                          handleDropOnFolder(folder.id);
                        }
                        setListDragIndex(null); setListDragOverIndex(null); setDragOverFolder(null);
                      }}
                      onDoubleClick={() => setCurrentFolderId(folder.id)}
                    >
                      {canEdit && (
                        <td className="px-2 py-1.5">
                          <GripVertical className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab" />
                        </td>
                      )}
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <Folder className="h-4 w-4 text-yellow-400 shrink-0" />
                          {renaming?.id === folder.id ? (
                            <Input
                              ref={renameInputRef}
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(null); }}
                              onBlur={handleRename}
                              className="h-6 text-sm px-1"
                              onClick={e => e.stopPropagation()}
                              data-testid={`input-rename-${folder.id}`}
                            />
                          ) : (
                            <span className="font-medium">{folder.nom}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell">Dossier</td>
                      <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell">
                        {folder.childCount !== undefined ? `${folder.childCount} élém.` : ""}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground hidden md:table-cell">{formatDate(folder.createdAt as unknown as string)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <FolderContextMenu folder={folder} />
                      </td>
                    </tr>
                  ))}

                  {/* Files rows */}
                  {files.map((file, idx) => {
                    const Icon = fileIcon(file.typeFichier, file.nom);
                    return (
                      <tr
                        key={file.id}
                        className={`group border-b hover:bg-muted/40 cursor-pointer transition-colors ${listDragOverIndex === idx && listDragIndex?.type === "file" ? "border-t-2 border-primary" : ""}`}
                        data-testid={`file-row-${file.id}`}
                        draggable
                        onDragStart={() => { setDraggedItem({ id: file.id, type: "file" }); setListDragIndex({ type: "file", index: idx }); }}
                        onDragEnd={() => { setDraggedItem(null); setListDragIndex(null); setListDragOverIndex(null); }}
                        onDragOver={e => { e.preventDefault(); setListDragOverIndex(idx); }}
                        onDrop={e => {
                          e.preventDefault();
                          if (listDragIndex?.type === "file" && listDragOverIndex !== null) {
                            handleListReorder("file", listDragIndex.index, idx);
                          }
                          setListDragIndex(null); setListDragOverIndex(null);
                        }}
                        onDoubleClick={() => window.open(file.fichierUrl, "_blank")}
                      >
                        {canEdit && (
                          <td className="px-2 py-1.5">
                            <GripVertical className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab" />
                          </td>
                        )}
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                            {renaming?.id === file.id ? (
                              <Input
                                ref={renameInputRef}
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(null); }}
                                onBlur={handleRename}
                                className="h-6 text-sm px-1"
                                onClick={e => e.stopPropagation()}
                                data-testid={`input-rename-${file.id}`}
                              />
                            ) : (
                              <span>{file.nom}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell">{file.typeFichier?.split("/")[1]?.toUpperCase() || "—"}</td>
                        <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell">{formatSize(file.taille)}</td>
                        <td className="px-2 py-1.5 text-muted-foreground hidden md:table-cell">{formatDate(file.createdAt as unknown as string)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <FileContextMenu file={file} />
                        </td>
                      </tr>
                    );
                  })}

                  {folders.length === 0 && files.length === 0 && !creatingFolder && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-muted-foreground text-sm">Dossier vide</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Upload queue */}
      {uploadQueue.length > 0 && (
        <div className="fixed bottom-4 right-4 w-72 space-y-1.5 z-50">
          {uploadQueue.map(item => (
            <div key={item.id} className="bg-background border rounded-lg p-2.5 shadow-md text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="truncate text-xs font-medium max-w-[200px]">{item.file.name}</span>
                {item.done && (
                  <button onClick={() => setUploadQueue(q => q.filter(i => i.id !== item.id))}>
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
              {item.error ? (
                <span className="text-destructive text-xs">{item.error}</span>
              ) : item.done ? (
                <span className="text-green-600 text-xs">✓ Terminé</span>
              ) : (
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Move Dialog */}
      {moveTarget && (
        <MoveDialog
          target={moveTarget}
          resellerId={resellerId}
          currentFolderId={currentFolderId}
          onConfirm={(fid) => { setMoveFolderId(fid); setTimeout(handleMove, 0); }}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {deleteTarget?.nom} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "folder"
                ? "Le dossier doit être vide pour être supprimé."
                : "Le fichier sera définitivement supprimé."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MoveDialog({
  target,
  resellerId,
  currentFolderId,
  onConfirm,
  onClose,
}: {
  target: { id: string; type: "folder" | "file"; nom: string };
  resellerId: string;
  currentFolderId: string | null;
  onConfirm: (folderId: string | null) => void;
  onClose: () => void;
}) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const { data } = useQuery<{ folders: ResellerFolder[] }>({
    queryKey: ["/api/resellers", resellerId, "folders", null, "move-picker"],
    queryFn: async () => {
      const r = await authFetch(`/api/resellers/${resellerId}/folders`);
      return r.json();
    },
  });

  const allFolders = data?.folders ?? [];

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Déplacer « {target.nom} »</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 max-h-60 overflow-y-auto">
          <button
            className={`w-full text-left px-3 py-2 rounded hover:bg-muted flex items-center gap-2 text-sm ${selectedFolderId === null ? "bg-muted font-medium" : ""}`}
            onClick={() => setSelectedFolderId(null)}
          >
            <Home className="h-4 w-4" /> Racine (Documents)
          </button>
          {allFolders.filter(f => f.id !== target.id).map(f => (
            <button
              key={f.id}
              className={`w-full text-left px-3 py-2 rounded hover:bg-muted flex items-center gap-2 text-sm ${selectedFolderId === f.id ? "bg-muted font-medium" : ""}`}
              onClick={() => setSelectedFolderId(f.id)}
            >
              <Folder className="h-4 w-4 text-yellow-400" /> {f.nom}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => { onConfirm(selectedFolderId); onClose(); }}>Déplacer ici</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
